import React, { useEffect, useState } from "react";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { PlusIcon, Search } from "lucide-react";
import {
  useGetAllFacilitySchedulesQuery,
  useGetFacilitiesQuery,
} from "@/services/faciltiesApi";
import usePagination from "@/hooks/use-pagination";
import Pagination from "@/components/ui/table/pagination-table";
import useTableData from "@/components/ui/table/data-table-logic";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import FacilityScheduleForm from "../modal/form";
import TableFilters from "@/components/ui/table/data-table-filter";
import {
  displayDate,
  displayValue,
  formatDate,
  formatToPKR,
} from "@/lib/helper";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { FacilitySchedule } from "@/types/facility/schedule";
import { DataTableRowActions } from "./data-table-row-actions";
import { DatePickerWithRange } from "@/components/ui/date-range/date-rangePicker";
import { Facility } from "@/types/facility";
import { useDeleteFacilityScheduleMutation } from "@/services/faciltiesApi";
import { toast } from "@/components/ui/use-toast";
import { ErrorType } from "@/types";

interface SearchCriteriaType {
  limit: number;
  offset: number;
  sort_order: string;
  sort_key: string;
  search_key?: string;
  type: string;
  status?: string;
  access_control?: string;
  bookable?: string;
  is_add_on?: string;
}

const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
  type: "facility_schedule",
};

export default function FacilityScheduleTable() {
  const facilitySchedule = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string)
          .facility_schedule ?? "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  const [deleteFacilitySchedule, { isLoading: deleteLoading }] =
    useDeleteFacilityScheduleMutation();

  const {
    searchCriteria,
    query,
    toggleSortOrder,
    setSearchCriteria,
    inputValue,
    setInputValue,
  } = useTableData(initialValue);

  const {
    data: scheduleData,
    isLoading,
    refetch,
  } = useGetAllFacilitySchedulesQuery(
    {
      query: query + "&load_add_ons=true",
      orgId: orgId,
    },
    {
      skip: query == "",
    }
  );

  const { data: facilitiesData } = useGetFacilitiesQuery(
    {
      query: query,
      orgId: orgId,
    },
    {
      skip: query == "",
    }
  );

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [action, setAction] = useState<"add" | "edit">("add");
  const [formData, setFormData] = useState<FacilitySchedule | undefined>(
    undefined
  );
  const [openFilter, setOpenFilter] = useState(false);
  const [filterData, setFilterData] = useState<Record<string, any>>({});

  const facilitiestableData = React.useMemo(() => {
    return Array.isArray(scheduleData?.data) ? scheduleData?.data : [];
  }, [scheduleData]);

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  const handleAddFacility = () => {
    setIsDialogOpen(true);
    setAction("add");
    setFormData(undefined);
  };

  const handleEditFacilitySchedule = (data: FacilitySchedule) => {
    setIsDialogOpen(true);
    setAction("edit");
    setFormData(data);
  };

  const handleDeleteFacilitySchedule = async (data: FacilitySchedule) => {
    try {
      const resp = await deleteFacilitySchedule(
        data.general_info.id as number
      ).unwrap();
      if (resp) {
        if (scheduleData?.filtered_counts == 1 && searchCriteria.offset > 0) {
          setSearchCriteria((prev) => ({
            ...prev,
            offset: prev.offset-1,
          }));
          setFilterData((prev) => ({ ...prev, offset: prev.offset-1 }));
        }
        setInputValue("");
        refetch();
        toast({
          variant: "success",
          title: "Facility Deleted Successfully",
        });
      }
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `${
            typedError.data?.detail ||
            (typedError.data as { message?: string }).message
          }`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `Something Went Wrong.`,
        });
      }
    }
  };

  const actionsColumn: ColumnDef<FacilitySchedule> = {
    id: "actions",
    header: "Actions",
    maxSize: 100,
    cell: ({ row }) => (
      <DataTableRowActions
        access={facilitySchedule}
        data={row.original}
        refetch={refetch}
        handleEdit={handleEditFacilitySchedule}
        handleDelete={handleDeleteFacilitySchedule}
      />
    ),
  };

  const columns: ColumnDef<FacilitySchedule>[] = [
    {
      accessorKey: "general_info.facility_name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Facility Name"
          sortKey="facility_name"
          sortable={true}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="normal-case cursor-pointer">
                    <span>
                      {displayValue(
                        `${row.original.general_info.facility_name}`.length > 20
                          ? `${row.original.general_info.facility_name}`.substring(
                              0,
                              20
                            ) + "..."
                          : `${row.original.general_info.facility_name}`
                      )}
                    </span>
                  </p>
                </TooltipTrigger>
                <TooltipContent className="mr-24">
                  <p className="normal-case text-sm w-fit ">
                    {displayValue(
                      `${row?.original.general_info.facility_name}`
                    )}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "general_info.member_only",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Booking Type"
          sortKey="member_only"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <p>
            {row.original.general_info.member_only ? "Member Only" : "Public"}
          </p>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "general_info.start_date",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Start Date"
          sortKey="start_date"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <p className="text-nowrap">
            {displayDate(row.original.general_info.start_date)}
          </p>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "general_info.end_date",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="End Date"
          sortKey="end_date"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <p className="text-nowrap">
            {displayDate(row.original.general_info.end_date)}
          </p>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },

    {
      accessorKey: "general_info.max_attendees",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Max Attendees"
          sortKey="max_attendees"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return <p>{row.original.general_info.max_attendees}</p>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "general_info.advance_booking_days",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Advance Booking Days"
          sortKey="advance_booking_days"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return <p>{row.original.general_info.advance_booking_days}</p>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "general_info.is_exclusive",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Exclusive Schedule"
          sortKey="is_exclusive"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return <p>{row.original.general_info.is_exclusive ? "Yes" : "No"}</p>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    // {
    //   accessorKey: "general_info.always_available",
    //   header: ({ column }) => (
    //     <DataTableColumnHeader
    //       column={column}
    //       title="Always Available"
    //       sortKey="always_available"
    //       searchCriteria={searchCriteria}
    //       toggleSortOrder={toggleSortOrder}
    //     />
    //   ),
    //   cell: ({ row }) => {
    //     return <p>{row.original.general_info.always_available ? "Yes" : "No"}</p>;
    //   },
    //   enableSorting: false,
    //   enableHiding: false,
    // },

    ...(facilitySchedule !== "read" ? [actionsColumn] : []),
  ];

  const table = useReactTable({
    data: facilitiestableData as FacilitySchedule[],
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });

  const totalRecords = scheduleData?.filtered_counts || 0;
  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination<SearchCriteriaType>({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  const handleFilterChange = (
    field: string,
    value: string | number | number[] | React.ChangeEvent<HTMLInputElement>
  ) => {
    // Update the filter state based on field and value
    setFilterData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const filterDisplay = [
    {
      type: "multiselect",
      name: "facility_ids",
      label: "Facility",
      options:
        facilitiesData?.data?.map((facility: Facility) => ({
          value: facility.id,
          label: facility.name,
        })) || [],
      function: (value: string) => handleFilterChange("facility_ids", value),
    },
    {
      type: "select",
      name: "member_only",
      label: "Booking Type",
      options: [
        { id: "true", name: "Member Only" },
        { id: "false", name: "Public" },
      ],
      function: (value: string) => handleFilterChange("member_only", value),
    },
  ];
  const handleDateRange = (dates: {
    start_date: Date | undefined;
    end_date: Date | undefined;
  }) => {
    const formattedStartDate = dates.start_date
      ? formatDate(dates.start_date)
      : "";
    const formattedEndDate = dates.end_date ? formatDate(dates.end_date) : "";

    setFilterData((prev) => ({
      ...prev,
      start_date: formattedStartDate,
      end_date: formattedEndDate,
    }));

    setSearchCriteria((prev: any) => ({
      ...prev,
      start_date: formattedStartDate ?? undefined,
      // Only set end_date if start_date is present
      end_date:
        formattedStartDate && !formattedEndDate ? undefined : formattedEndDate,
      offset: 0,
      sort_key: "id",
      sort_order: "desc",
    }));
  };

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 px-3">
        {/* Table Filter component */}
        <TableSearchFilter
          setSearchCriteria={setSearchCriteria}
          inputValue={inputValue}
          setInputValue={setInputValue}
        />

        {/* Buttons Container */}
        <div className="flex flex-row lg:flex-row lg:justify-center lg:items-center gap-2">
          <div className="text-sm  text-black flex items-center gap-1  lg:mb-0 h-8">
            <DatePickerWithRange
              name={"Date Range"}
              value={{
                start_date: filterData.start_date, // Access start_date directly
                end_date: filterData.end_date, // Access end_date directly
              }}
              onValueChange={(dates) => {
                handleDateRange(dates);
              }}
              label={"Select date range"}
              className="w-full" // Ensure full width
            />
          </div>
          <Button
            variant={"outline"}
            className="text-sm  text-black flex items-center gap-1  lg:mb-0 h-8 px-2"
            onClick={() => {
              setFilterData({});
              setSearchCriteria(initialValue);
              setInputValue("");
            }}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="gap-2 flex justify-center items-center">
                    <i className="fa-solid fa-arrows-rotate"></i>
                    Reset
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Reset Filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </Button>
          <Button
            access={facilitySchedule !== "read"}
            className="bg-primary text-sm  text-black flex items-center gap-1  lg:mb-0 h-8 px-2"
            onClick={handleAddFacility}
          >
            <PlusIcon className="h-4 w-4 " />
            Create New
          </Button>
          <button
            className={`border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center ${
              Object.keys(filterData).length ? "border-primary" : ""
            }`}
            onClick={() => setOpenFilter(true)}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <i
                    className={`fa fa-filter ${
                      Object.keys(filterData).length ? "text-primary" : ""
                    }`}
                  ></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>click to apply filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>
        </div>
      </div>
      <div className="rounded-none border border-border  ">
        <ScrollArea className="w-full relative" hideScrollToTop={true}>
          <ScrollBar
            orientation="horizontal"
            className="relative z-30 cursor-grab h-2"
          />
          {/* <Table className="w-full overflow-x-scroll"> */}
          <Table className="w-full sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]  ">
            {" "}
            {/* //overflow-y-auto scrollbar-thin */}
            <TableHeader className="bg-secondary">
              {table?.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    <div className="flex space-x-2 justify-center items-center bg-white ">
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={(e) => {
                      if (
                        facilitySchedule !== "read" &&
                        e.target instanceof Element &&
                        !e.target.closest('[role="checkbox"]') &&
                        (e.target === e.currentTarget || e.target.closest("td"))
                      ) {
                        handleEditFacilitySchedule(
                          row.original as unknown as FacilitySchedule
                        );
                      }
                    }}
                    className={
                      facilitySchedule !== "read"
                        ? "cursor-pointer hover:bg-secondary"
                        : ""
                    }
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : facilitiestableData.length > 0 ? (
                <TableRow className="!p-0">
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center  "
                  >
                    No facility found.
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No records found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {/* pagination */}
      {facilitiestableData.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}

      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={setSearchCriteria}
        filterDisplay={filterDisplay}
        setInputValue={setInputValue}
      />

      <FacilityScheduleForm
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
        data={formData}
        action={action}
        setAction={setAction}
        setData={setFormData}
        refetch={refetch}
      />
    </div>
  );
}
